/**
 * Presenting the deck.
 *
 * On the website this still covers the window, because a browser tab cannot
 * own a second display. On the Mac, a second monitor gets a dedicated window
 * so the manuscript stays on the laptop — which is the thing being read from.
 * A single display falls back to covering this window, using the OS fullscreen
 * rather than the webview's request, because WKWebView's fullscreen is a
 * different gesture from the one a preacher already knows.
 *
 * Painted by `PresenterStage` / `drawSlide`, the same function that writes the
 * PNGs, so what the congregation sees is what the file contains.
 *
 * Keys are the AV-desk set: every ordinary next/back key, B to blank, Escape
 * to finish. Capture phase, so the editor never sees a presentation key.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { ChevronLeft, ChevronRight, Maximize2, Minimize2, X } from "lucide-react";
import { getCurrentWindow } from "@tauri-apps/api/window";
import { PresenterStage } from "@/components/editor/PresenterStage";
import { useWakeLock } from "@/hooks/use-wake-lock";
import { isDesktopApp } from "@/lib/runtime";
import type { Slide } from "@/lib/slides";
import type { SlideStyle } from "@/lib/slide-themes";
import {
  closePresenterWindow,
  listenPresenterControl,
  listenPresenterReady,
  openPresenterWindow,
  pushPresenterState,
} from "@/lib/presenter-window";
import { cn } from "@/lib/utils";

const CHROME_TIMEOUT_MS = 2500;

interface SlidePresenterProps {
  slides: Slide[];
  startIndex: number;
  open: boolean;
  onClose: () => void;
  style: SlideStyle;
}

function Controls({
  index,
  total,
  blanked,
  atStart,
  atEnd,
  title,
  chromeVisible,
  isFullscreen,
  onPrev,
  onNext,
  onBlank,
  onFullscreen,
  onClose,
}: {
  index: number;
  total: number;
  blanked: boolean;
  atStart: boolean;
  atEnd: boolean;
  title: string;
  chromeVisible: boolean;
  isFullscreen: boolean;
  onPrev: () => void;
  onNext: () => void;
  onBlank: () => void;
  onFullscreen?: () => void;
  onClose: () => void;
}) {
  return (
    <div
      className={cn(
        "pointer-events-none absolute inset-x-0 bottom-0 flex items-center gap-2 p-4 transition-opacity duration-300",
        chromeVisible ? "opacity-100" : "opacity-0",
      )}
    >
      <div
        className="pointer-events-auto flex items-center gap-1 rounded-full bg-white/10 px-2 py-1 backdrop-blur"
        onClick={(event) => event.stopPropagation()}
      >
        <button
          type="button"
          onClick={onPrev}
          disabled={atStart}
          aria-label="Previous slide"
          className="rounded-full p-2 text-white/80 hover:bg-white/15 hover:text-white disabled:opacity-30 focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
        >
          <ChevronLeft className="h-5 w-5" />
        </button>
        <span className="min-w-16 text-center text-sm tabular-nums text-white/80">
          {total ? `${index + 1} / ${total}` : "—"}
        </span>
        <button
          type="button"
          onClick={onNext}
          disabled={atEnd}
          aria-label="Next slide"
          className="rounded-full p-2 text-white/80 hover:bg-white/15 hover:text-white disabled:opacity-30 focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
        >
          <ChevronRight className="h-5 w-5" />
        </button>
      </div>

      <span className="hidden flex-1 truncate px-2 text-sm text-white/50 sm:block">
        {blanked ? "Screen blanked — any key brings it back" : title}
      </span>

      <div
        className="pointer-events-auto flex items-center gap-1 rounded-full bg-white/10 px-2 py-1 backdrop-blur"
        onClick={(event) => event.stopPropagation()}
      >
        <button
          type="button"
          onClick={onBlank}
          aria-pressed={blanked}
          className={cn(
            "rounded-full px-3 py-1.5 text-xs focus:outline-none focus-visible:ring-2 focus-visible:ring-white",
            blanked ? "bg-white text-black" : "text-white/80 hover:bg-white/15 hover:text-white",
          )}
        >
          Blank
        </button>
        {onFullscreen && (
          <button
            type="button"
            onClick={onFullscreen}
            aria-label={isFullscreen ? "Leave full screen" : "Full screen"}
            className="rounded-full p-2 text-white/80 hover:bg-white/15 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
          >
            {isFullscreen ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
          </button>
        )}
        <button
          type="button"
          onClick={onClose}
          aria-label="Finish presenting"
          className="rounded-full p-2 text-white/80 hover:bg-white/15 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

export function SlidePresenter({
  slides,
  startIndex,
  open,
  onClose,
  style,
}: SlidePresenterProps) {
  const [index, setIndex] = useState(startIndex);
  const [blanked, setBlanked] = useState(false);
  const [chromeVisible, setChromeVisible] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [remote, setRemote] = useState(false);
  /** Until the other display is probed, covering this window would flash the manuscript away. */
  const [display, setDisplay] = useState<"pending" | "remote" | "local">("pending");

  const surfaceRef = useRef<HTMLDivElement>(null);
  const chromeTimer = useRef<number | undefined>(undefined);
  const remoteRef = useRef(false);
  remoteRef.current = remote;

  useWakeLock(open);

  const total = slides.length;
  const slide = slides[Math.min(index, Math.max(0, total - 1))];

  useEffect(() => {
    if (open) {
      setIndex(Math.max(0, Math.min(startIndex, total - 1)));
      setBlanked(false);
    }
  }, [open, startIndex, total]);

  const showChrome = useCallback(() => {
    setChromeVisible(true);
    window.clearTimeout(chromeTimer.current);
    chromeTimer.current = window.setTimeout(() => setChromeVisible(false), CHROME_TIMEOUT_MS);
  }, []);

  useEffect(() => {
    if (!open) return;
    showChrome();
    return () => window.clearTimeout(chromeTimer.current);
  }, [open, showChrome]);

  const go = useCallback(
    (delta: number) => {
      setBlanked(false);
      setIndex((current) => Math.max(0, Math.min(total - 1, current + delta)));
    },
    [total],
  );

  const toggleFullscreen = useCallback(() => {
    if (isDesktopApp) {
      void getCurrentWindow()
        .isFullscreen()
        .then((full) => getCurrentWindow().setFullscreen(!full));
      return;
    }
    const element = surfaceRef.current;
    if (!element) return;
    if (document.fullscreenElement) {
      void document.exitFullscreen().catch(() => {});
    } else {
      void element.requestFullscreen?.().catch(() => {});
    }
  }, []);

  useEffect(() => {
    if (!open) {
      setRemote(false);
      setDisplay("pending");
      return;
    }
    if (!isDesktopApp) {
      setRemote(false);
      setDisplay("local");
      return;
    }

    let cancelled = false;
    setDisplay("pending");
    const state = {
      slides,
      index: Math.max(0, Math.min(startIndex, slides.length - 1)),
      blanked: false,
      style,
    };

    void openPresenterWindow(state)
      .then((opened) => {
        if (cancelled) return;
        setRemote(opened);
        setDisplay(opened ? "remote" : "local");
      })
      .catch(() => {
        if (cancelled) return;
        setRemote(false);
        setDisplay("local");
      });

    const ready = listenPresenterReady(() => {
      void pushPresenterState({
        slides,
        index: remoteRef.current ? index : Math.max(0, Math.min(startIndex, slides.length - 1)),
        blanked,
        style,
      });
    });
    const controls = listenPresenterControl((control) => {
      if (control === "next") go(1);
      else if (control === "prev") go(-1);
      else if (control === "blank") setBlanked((current) => !current);
      else if (control === "home") {
        setBlanked(false);
        setIndex(0);
      } else if (control === "end") {
        setBlanked(false);
        setIndex(Math.max(0, total - 1));
      } else if (control === "close") {
        onClose();
      }
    });

    return () => {
      cancelled = true;
      void ready.then((unlisten) => unlisten());
      void controls.then((unlisten) => unlisten());
      void closePresenterWindow();
    };
    // Opening is keyed to `open` so a slide change mid-deck does not recreate
    // the window. State is pushed separately below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open || !remote) return;
    void pushPresenterState({ slides, index, blanked, style });
  }, [open, remote, slides, index, blanked, style]);

  useEffect(() => {
    if (!open || display !== "local") return;

    if (isDesktopApp) {
      void getCurrentWindow().setFullscreen(true);
      const window = getCurrentWindow();
      const unlisten = window.onResized(() => {
        void window.isFullscreen().then(setIsFullscreen);
      });
      void window.isFullscreen().then(setIsFullscreen);
      return () => {
        void unlisten.then((fn) => fn());
        void getCurrentWindow().setFullscreen(false);
      };
    }

    const element = surfaceRef.current;
    void element?.requestFullscreen?.().catch(() => {});
    const onChange = () => setIsFullscreen(Boolean(document.fullscreenElement));
    document.addEventListener("fullscreenchange", onChange);
    onChange();
    return () => {
      document.removeEventListener("fullscreenchange", onChange);
      if (document.fullscreenElement) void document.exitFullscreen().catch(() => {});
    };
  }, [open, display]);

  useEffect(() => {
    if (!open) return;

    const onKeyDown = (event: KeyboardEvent) => {
      const handled = () => {
        event.preventDefault();
        event.stopPropagation();
        showChrome();
      };

      switch (event.key) {
        case "ArrowRight":
        case "ArrowDown":
        case "PageDown":
        case " ":
        case "Enter":
        case "n":
        case "N":
          handled();
          go(1);
          break;
        case "ArrowLeft":
        case "ArrowUp":
        case "PageUp":
        case "Backspace":
        case "p":
        case "P":
          handled();
          go(-1);
          break;
        case "Home":
          handled();
          setBlanked(false);
          setIndex(0);
          break;
        case "End":
          handled();
          setBlanked(false);
          setIndex(Math.max(0, total - 1));
          break;
        case "b":
        case "B":
        case ".":
          handled();
          setBlanked((current) => !current);
          break;
        case "f":
        case "F":
          if (!remote) {
            handled();
            toggleFullscreen();
          }
          break;
        default:
          break;
      }
    };

    window.addEventListener("keydown", onKeyDown, true);
    return () => window.removeEventListener("keydown", onKeyDown, true);
  }, [open, go, total, showChrome, toggleFullscreen, remote]);

  const atStart = index <= 0;
  const atEnd = index >= total - 1;
  const controlProps = {
    index,
    total,
    blanked,
    atStart,
    atEnd,
    title: slide?.title ?? "",
    onPrev: () => go(-1),
    onNext: () => go(1),
    onBlank: () => setBlanked((current) => !current),
    onClose,
  };

  if (!open || display === "pending") return null;

  if (display === "remote") {
    return (
      <div className="pointer-events-none fixed inset-x-0 bottom-4 z-[60] flex justify-center px-4">
        <div
          className="pointer-events-auto flex w-full max-w-xl items-center gap-3 rounded-2xl bg-zinc-950/90 px-3 py-2 text-white shadow-xl"
          onPointerMove={showChrome}
        >
          <button type="button" onClick={() => go(-1)} disabled={atStart} aria-label="Previous slide" className="rounded-full p-2 hover:bg-white/10 disabled:opacity-30">
            <ChevronLeft className="h-5 w-5" />
          </button>
          <span className="min-w-14 text-center text-sm tabular-nums text-white/80">
            {total ? `${index + 1} / ${total}` : "—"}
          </span>
          <button type="button" onClick={() => go(1)} disabled={atEnd} aria-label="Next slide" className="rounded-full p-2 hover:bg-white/10 disabled:opacity-30">
            <ChevronRight className="h-5 w-5" />
          </button>
          <span className="min-w-0 flex-1 truncate text-xs text-white/55">
            {blanked ? "Screen blanked" : "Presenting on the other display"}
          </span>
          <button
            type="button"
            onClick={() => setBlanked((current) => !current)}
            className={cn(
              "rounded-full px-3 py-1.5 text-xs",
              blanked ? "bg-white text-black" : "hover:bg-white/10",
            )}
          >
            Blank
          </button>
          <button type="button" onClick={onClose} aria-label="Finish presenting" className="rounded-full p-2 hover:bg-white/10">
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <DialogPrimitive.Root
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <DialogPrimitive.Portal>
        <DialogPrimitive.Content
          ref={surfaceRef}
          aria-label="Slide presentation"
          className="fixed inset-0 z-[60] bg-black outline-none"
          onPointerMove={showChrome}
          onClick={() => go(1)}
        >
          <DialogPrimitive.Title className="sr-only">Slide presentation</DialogPrimitive.Title>
          <DialogPrimitive.Description className="sr-only">
            Slide {index + 1} of {total}. Arrow keys or space to move, B to blank the screen,
            Escape to finish.
          </DialogPrimitive.Description>

          <PresenterStage slides={slides} index={index} blanked={blanked} style={style} />

          <Controls
            {...controlProps}
            chromeVisible={chromeVisible}
            isFullscreen={isFullscreen}
            onFullscreen={toggleFullscreen}
          />
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
